import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Shield, Eye, Database, Cookie, UserCheck, Mail } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const PrivacyPage = () => {
  usePageSEO();
  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fdf2f4] rounded-full mb-4">
              <Shield className="w-8 h-8 text-[#b89a6d]" />
            </div>
            <h1 className="text-4xl font-bold text-[#3a2a26] mb-4">Privacy Policy</h1>
            <p className="text-gray-400">Last updated: January 31, 2026</p>
          </div>

          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                PeptidesForMedSpas.com ("we," "our," or "us") respects your privacy and is committed 
                to protecting your personal information. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Database className="w-6 h-6 text-[#b89a6d] mr-3" />
                Information We Collect
              </h2>
              <div className="text-gray-300 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#3a2a26] mb-2">Information You Provide</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Name and email address when using our contact form</li>
                    <li>Any messages or feedback you send to us</li>
                    <li>Newsletter subscription information (if applicable)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#3a2a26] mb-2">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>IP address and browser type</li>
                    <li>Device information and operating system</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website information</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Information */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Eye className="w-6 h-6 text-[#b89a6d] mr-3" />
                How We Use Your Information
              </h2>
              <ul className="text-gray-300 space-y-3">
                <li className="flex items-start">
                  <span className="text-[#b89a6d] mr-3">•</span>
                  <span>To respond to your inquiries and provide customer support</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#b89a6d] mr-3">•</span>
                  <span>To improve our website content and user experience</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#b89a6d] mr-3">•</span>
                  <span>To analyze website traffic and usage patterns</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#b89a6d] mr-3">•</span>
                  <span>To send periodic emails (if you've opted in)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#b89a6d] mr-3">•</span>
                  <span>To comply with legal obligations</span>
                </li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Cookie className="w-6 h-6 text-[#b89a6d] mr-3" />
                Cookies and Tracking Technologies
              </h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  We use cookies and similar tracking technologies to track activity on our website 
                  and hold certain information. Cookies are files with a small amount of data that 
                  may include an anonymous unique identifier.
                </p>
                <div>
                  <h3 className="text-lg font-semibold text-[#3a2a26] mb-2">Types of Cookies We Use</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-[#3a2a26]">Essential Cookies:</strong> Required for basic site functionality</li>
                    <li><strong className="text-[#3a2a26]">Analytics Cookies:</strong> Help us understand how visitors use our site (Google Analytics)</li>
                    <li><strong className="text-[#3a2a26]">Preference Cookies:</strong> Remember your settings and preferences</li>
                  </ul>
                </div>
                <p>
                  You can instruct your browser to refuse all cookies or to indicate when a cookie 
                  is being sent. However, some parts of the site may not function properly without cookies.
                </p>
              </div>
            </section>

            {/* Third-Party Services */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Third-Party Services</h2>
              <div className="text-gray-300 space-y-4">
                <p>We may use third-party services that collect, monitor, and analyze data:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-[#3a2a26]">Google Analytics:</strong> For website traffic analysis</li>
                  <li><strong className="text-[#3a2a26]">Affiliate Networks:</strong> For tracking referral commissions</li>
                </ul>
                <p>
                  These third parties have their own privacy policies governing how they use your information. 
                  We encourage you to review their policies.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#b89a6d] mb-4 flex items-center">
                <UserCheck className="w-6 h-6 text-[#b89a6d] mr-3" />
                Your Privacy Rights
              </h2>
              <div className="text-gray-300 space-y-4">
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt out of marketing communications</li>
                  <li>Withdraw consent where applicable</li>
                </ul>
                <p>
                  To exercise these rights, please contact us using the information below.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Data Security</h2>
              <p className="text-gray-300 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect 
                your personal information. However, no method of transmission over the Internet or 
                electronic storage is 100% secure. While we strive to protect your information, we 
                cannot guarantee its absolute security.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Children's Privacy</h2>
              <p className="text-gray-300 leading-relaxed">
                Our website is not intended for individuals under 18 years of age. We do not knowingly 
                collect personal information from children. If you are a parent or guardian and believe 
                your child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Changes to This Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new Privacy Policy on this page and updating the "Last updated" date. 
                You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Mail className="w-6 h-6 text-[#b89a6d] mr-3" />
                Contact Us
              </h2>
              <p className="text-gray-300">
                If you have questions about this Privacy Policy, please contact us at:{" "}
                <a 
                  href="mailto:info@peptidesciences.ai" 
                  className="text-[#b89a6d] hover:text-[#a08560] underline"
                >
                  info@peptidesciences.ai
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
